/**
 * Audit Page - Platform Audit Logs
 * 
 * Displays all audit logs for platform administration.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Search, RefreshCw, Filter, Download,
  Calendar, User, Building2, Shield, Activity,
  ExternalLink, CreditCard, Settings, Pause, Play
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../../components/ui/popover';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const actionIcons = {
  CREATE_RIAD: Building2,
  UPDATE_RIAD: Settings,
  SUSPEND_RIAD: Pause,
  REACTIVATE_RIAD: Play,
  DELETE_RIAD: Building2,
  DISABLE_RIAD: Building2,
  UPDATE_SUBSCRIPTION: CreditCard,
  EXTEND_SUBSCRIPTION: CreditCard,
  CHANGE_SUBSCRIPTION_PLAN: CreditCard,
  ADD_USER_TO_RIAD: User,
  REMOVE_USER_FROM_RIAD: User,
  UPDATE_USER_RIAD_ACCESS: User,
  CREATE_PLAN: Shield,
  UPDATE_PLAN: Shield,
  DELETE_PLAN: Shield,
  DUPLICATE_PLAN: Shield,
  OPEN_TENANT: ExternalLink,
  CREATE_BACKUP: FileText,
  DELETE_BACKUP: FileText,
  SUSPEND_ACCOUNT: Pause,
  REACTIVATE_ACCOUNT: Play
};

const actionColors = {
  CREATE_RIAD: 'bg-green-100 text-green-800',
  UPDATE_RIAD: 'bg-blue-100 text-blue-800',
  SUSPEND_RIAD: 'bg-orange-100 text-orange-800',
  REACTIVATE_RIAD: 'bg-green-100 text-green-800',
  DELETE_RIAD: 'bg-red-100 text-red-800',
  DISABLE_RIAD: 'bg-red-100 text-red-800',
  UPDATE_SUBSCRIPTION: 'bg-violet-100 text-violet-800',
  EXTEND_SUBSCRIPTION: 'bg-green-100 text-green-800',
  CHANGE_SUBSCRIPTION_PLAN: 'bg-violet-100 text-violet-800',
  ADD_USER_TO_RIAD: 'bg-blue-100 text-blue-800',
  REMOVE_USER_FROM_RIAD: 'bg-red-100 text-red-800',
  UPDATE_USER_RIAD_ACCESS: 'bg-blue-100 text-blue-800',
  CREATE_PLAN: 'bg-green-100 text-green-800',
  UPDATE_PLAN: 'bg-blue-100 text-blue-800',
  DELETE_PLAN: 'bg-red-100 text-red-800',
  OPEN_TENANT: 'bg-purple-100 text-purple-800',
  CREATE_BACKUP: 'bg-green-100 text-green-800',
  DELETE_BACKUP: 'bg-red-100 text-red-800',
  SUSPEND_ACCOUNT: 'bg-orange-100 text-orange-800',
  REACTIVATE_ACCOUNT: 'bg-green-100 text-green-800'
};

const AuditPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, pagination.page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 50);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);

      const { data } = await api.get(`/admin/audit?${params}`);
      
      // Filter by search client-side
      let filtered = data.logs;
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(log => 
          log.action?.toLowerCase().includes(searchLower) ||
          log.user?.email?.toLowerCase().includes(searchLower) ||
          log.description?.toLowerCase().includes(searchLower)
        );
      }
      
      setLogs(filtered);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    fetchLogs();
  };

  const handleExport = () => {
    // Generate CSV export
    const headers = ['Date', 'Action', 'Utilisateur', 'Riad', 'Description'];
    const rows = logs.map(log => [
      format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss'),
      log.action,
      log.user?.email || 'Système',
      log.riad_id || '-',
      log.description || '-'
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Export téléchargé');
  };

  // Get unique actions for filter
  const uniqueActions = [...new Set(logs.map(l => l.action))].sort();

  // Stats
  const stats = {
    total: pagination.total,
    today: logs.filter(l => 
      format(new Date(l.createdAt), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
    ).length,
    openTenant: logs.filter(l => l.action === 'OPEN_TENANT').length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Journal d'Audit</h1>
          <p className="text-muted-foreground">Historique des actions d'administration</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Exporter CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Logs totaux</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.today}</p>
              <p className="text-sm text-muted-foreground">Aujourd'hui</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <ExternalLink className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.openTenant}</p>
              <p className="text-sm text-muted-foreground">Ouvertures tenant</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher dans les logs..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="audit-search-input"
            />
          </div>
        </form>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[200px]" data-testid="action-filter">
            <SelectValue placeholder="Type d'action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            <SelectItem value="OPEN_TENANT">Ouverture tenant</SelectItem>
            <SelectItem value="CREATE_RIAD">Création riad</SelectItem>
            <SelectItem value="UPDATE_RIAD">Modification riad</SelectItem>
            <SelectItem value="SUSPEND_RIAD">Suspension riad</SelectItem>
            <SelectItem value="CHANGE_SUBSCRIPTION_PLAN">Changement plan</SelectItem>
            <SelectItem value="ADD_USER_TO_RIAD">Ajout accès</SelectItem>
            <SelectItem value="REMOVE_USER_FROM_RIAD">Suppression accès</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon">
              <Filter className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-4">
              <h4 className="font-medium">Filtrer par date</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Du</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Au</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <Button onClick={fetchLogs} className="w-full">Appliquer</Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" size="icon" onClick={fetchLogs}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Riad</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Détails</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    Aucun log trouvé
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => {
                  const ActionIcon = actionIcons[log.action] || Activity;
                  const details = log.details || {};
                  
                  return (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div>
                          <p className="font-medium">
                            {format(new Date(log.createdAt), 'dd/MM/yyyy')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(log.createdAt), 'HH:mm:ss')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.user ? (
                          <div>
                            <p className="font-medium">
                              {log.user.first_name} {log.user.last_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{log.user.email}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Système</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={actionColors[log.action] || 'bg-gray-100'}>
                          <ActionIcon className="w-3 h-3 mr-1" />
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {log.riad_id ? (
                          <span 
                            className="text-blue-600 hover:underline cursor-pointer"
                            onClick={() => navigate(`/admin/riads/${log.riad_id}`)}
                          >
                            {details.riad_name || `Riad #${log.riad_id}`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {details.account_id ? (
                          <span 
                            className="text-violet-600 hover:underline cursor-pointer"
                            onClick={() => navigate(`/admin/customers/${details.account_id}`)}
                          >
                            {details.account_name || `Account #${details.account_id}`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-muted-foreground truncate">
                          {log.description || 
                           details.reason || 
                           details.user_email ||
                           (details.changes ? `Modifié: ${Object.keys(details.changes).join(', ')}` : '-')
                          }
                        </p>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} / {pagination.pages}
          </span>
          <Button
            variant="outline"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
